import { useEffect, useMemo, useRef, useState, Children } from "react";
import { Link } from "react-router-dom";
import NeutralHeader from "@/components/NeutralHeader";
import MinimalFooter from "@/components/MinimalFooter";
import { ArrowRight, ChevronRight, Gauge, Sprout, Target, Mountain } from "lucide-react";

const COUPLE_IMG =
  "https://images.unsplash.com/photo-1508963493744-76fce69379c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const ALL_GUIDES = [
  { to: "/how-the-score-works",         label: "How Your Score Is Calculated",         read: "4 min read", icon: Gauge,    tint: { bg: "#eaf2fa", fg: "#1E1968" } },
  { to: "/financial-planning-20s-30s",  label: "Financial Planning in Your 20s & 30s", read: "7 min read", icon: Sprout,   tint: { bg: "#e8f3ec", fg: "#2f6b4a" } },
  { to: "/financial-planning-40s-50s",  label: "Financial Planning in Your 40s & 50s", read: "7 min read", icon: Target,   tint: { bg: "#fdf4e4", fg: "#9a6a1f" } },
  { to: "/retirement-readiness-guide",  label: "Retirement Readiness Guide",           read: "8 min read", icon: Mountain, tint: { bg: "#f3edf9", fg: "#5b3f8a" } },
];

const slug = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .slice(0, 60);

/**
 * ContentPage — guide layout aligned with the attached design mockup.
 *
 * Props (all optional — defaults preserve legacy behavior for non-guide pages):
 *   • cta, ctaTo              bottom CTA button
 *   • breadcrumbs             array of {to, label} shown above the title
 *   • readMinutes             int displayed as "X min read" meta
 *   • lastUpdated             string displayed next to read-time
 *   • showHeroImage           bool — show the older-couple image in hero (defaults true for guides)
 *   • related                 array of {to, label} rendered in the sidebar; defaults to all other guides
 *   • variant                 "guide" (default) or "plain" (no sidebar, used for Privacy)
 *
 * Children usage stays identical to the old API — pass <Eyebrow/>, <PageTitle/>, <Lead/>, <H2/>, <P/>, <UL/>.
 * The H2 elements are automatically discovered and listed in the sticky "In This Article" sidebar.
 */
export const ContentPage = ({
  children,
  cta = "Take the Assessment",
  ctaTo = "/assessment",
  breadcrumbs,
  readMinutes,
  lastUpdated,
  showHeroImage = true,
  related,
  variant = "guide",
}) => {
  const articleRef = useRef(null);
  const [toc, setToc] = useState([]);
  const [activeId, setActiveId] = useState(null);

  // Build the TOC by scanning the rendered <h2> elements inside the article.
  useEffect(() => {
    if (variant !== "guide" || !articleRef.current) return;
    const headings = Array.from(articleRef.current.querySelectorAll("h2"));
    const items = headings.map((h, i) => {
      const id = h.id || slug(h.innerText) || `section-${i + 1}`;
      h.id = id;
      return { id, label: h.innerText, index: i + 1 };
    });
    setToc(items);
  }, [variant, children]);

  // Highlight the TOC item nearest the top of the viewport while scrolling.
  useEffect(() => {
    if (!toc.length) return;
    const onScroll = () => {
      let currentId = toc[0].id;
      for (const t of toc) {
        const el = document.getElementById(t.id);
        if (el && el.getBoundingClientRect().top <= 140) currentId = t.id;
      }
      setActiveId(currentId);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [toc]);

  const relatedList = useMemo(() => {
    if (Array.isArray(related)) return related;
    if (!breadcrumbs) return [];
    const currentPath = breadcrumbs[breadcrumbs.length - 1]?.to;
    return ALL_GUIDES.filter((g) => g.to !== currentPath).slice(0, 3);
  }, [related, breadcrumbs]);

  const Wrapper = variant === "guide" ? GuideWrapper : PlainWrapper;

  return (
    <div className="min-h-screen flex flex-col">
      <NeutralHeader />
      <main id="main-content" tabIndex={-1} className="flex-1" data-testid="content-main">
        <Wrapper
          breadcrumbs={breadcrumbs}
          readMinutes={readMinutes}
          lastUpdated={lastUpdated}
          showHeroImage={showHeroImage}
          toc={toc}
          activeId={activeId}
          related={relatedList}
          articleRef={articleRef}
          cta={cta}
          ctaTo={ctaTo}
        >
          {children}
        </Wrapper>
      </main>
      <MinimalFooter />
    </div>
  );
};

function Breadcrumbs({ items }) {
  if (!items?.length) return null;
  return (
    <nav aria-label="Breadcrumb" className="breadcrumbs" data-testid="guide-breadcrumbs">
      {items.map((b, i) => {
        const last = i === items.length - 1;
        return (
          <span key={b.to + i} style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
            {last ? (
              <span style={{ color: "#1E1968", fontWeight: 500 }}>{b.label}</span>
            ) : (
              <Link to={b.to}>{b.label}</Link>
            )}
            {!last && <ChevronRight className="sep" style={{ width: 14, height: 14 }} aria-hidden="true" />}
          </span>
        );
      })}
    </nav>
  );
}

/**
 * Guide variant: 2-column layout with sticky sidebar TOC + Related Guides.
 * The hero area shows breadcrumbs → title → lead → meta on the left and (optionally)
 * the older-couple image on the right.
 */
function GuideWrapper({ breadcrumbs, readMinutes, lastUpdated, showHeroImage, toc, activeId, related, articleRef, cta, ctaTo, children }) {
  // Split children into hero (eyebrow/title/lead) and article (body) so each piece
  // renders exactly once in the correct column.
  const heroSlot = [];
  const bodySlot = [];
  Children.forEach(children, (child, i) => {
    if (!child || typeof child !== "object" || !child.type) {
      bodySlot.push(child);
      return;
    }
    const name = child.type.displayName || child.type.name || "";
    if (name === "Eyebrow" || name === "PageTitle" || name === "Lead") {
      heroSlot.push(child);
    } else {
      bodySlot.push(child);
    }
  });

  return (
    <>
      {/* HERO */}
      <section style={{ background: "#ffffff", borderBottom: "1px solid #e6edf5" }}>
        <div className="summit-container pt-10 pb-10 lg:pt-14 lg:pb-12">
          <Breadcrumbs items={breadcrumbs} />
          <div className="mt-6 grid lg:grid-cols-12 gap-8 items-start">
            <div className={showHeroImage ? "lg:col-span-7" : "lg:col-span-12"}>
              {heroSlot}
              {(readMinutes || lastUpdated) && (
                <div className="mt-5 text-sm" style={{ color: "#3a4d70" }} data-testid="guide-meta">
                  {readMinutes && <span><strong style={{ color: "#17233D" }}>{readMinutes} min read</strong></span>}
                  {readMinutes && lastUpdated && <span aria-hidden="true" style={{ padding: "0 0.5rem" }}>·</span>}
                  {lastUpdated && <span>Last updated {lastUpdated}</span>}
                </div>
              )}
            </div>
            {showHeroImage && (
              <div className="lg:col-span-5">
                <div style={{ background: "#ffffff", padding: 8, boxShadow: "0 10px 28px rgba(23, 35, 61, 0.08)" }}>
                  <img src={COUPLE_IMG} alt="" loading="eager" className="w-full h-[240px] md:h-[320px] object-cover" style={{ display: "block" }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* BODY: article + sidebar */}
      <section style={{ background: "#ffffff" }}>
        <div className="summit-container py-12 lg:py-16">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <article
              ref={articleRef}
              className="lg:col-span-8"
              data-testid="guide-article"
              style={{ color: "#17233D", lineHeight: 1.72 }}
            >
              {bodySlot}
              <div className="mt-12 pt-8" style={{ borderTop: "1px solid #e6edf5" }}>
                <Link to={ctaTo} className="btn-primary" data-testid="content-cta">
                  {cta} <ArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />
                </Link>
              </div>
            </article>

            <aside className="lg:col-span-4 lg:sticky" style={{ top: 24 }} data-testid="guide-sidebar">
              {toc.length > 0 && (
                <div style={{ background: "#eaf2fa", padding: "1.25rem 1.25rem", border: "1px solid #cfe0f1" }} data-testid="guide-toc">
                  <div className="font-serif" style={{ color: "#1E1968", fontSize: "1.05rem", fontWeight: 600, lineHeight: 1.25 }}>
                    In This Article
                  </div>
                  <ol className="mt-4 space-y-2.5" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {toc.map((t) => {
                      const active = t.id === activeId;
                      return (
                        <li key={t.id} data-testid={`toc-${t.id}`} style={{ borderLeft: active ? "2px solid #258FC4" : "2px solid transparent", paddingLeft: 10 }}>
                          <a
                            href={`#${t.id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              const el = document.getElementById(t.id);
                              if (el) {
                                el.scrollIntoView({ behavior: "smooth", block: "start" });
                                el.setAttribute("tabindex", "-1");
                                el.focus({ preventScroll: true });
                              }
                            }}
                            style={{
                              color: active ? "#1E1968" : "#17233D",
                              textDecoration: "none",
                              fontSize: "0.92rem",
                              lineHeight: 1.45,
                              display: "block",
                              fontWeight: active ? 600 : 400,
                            }}
                          >
                            <span style={{ color: "#258FC4", marginRight: 6 }}>{t.index}.</span>
                            {t.label}
                          </a>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              )}

              {related.length > 0 && (
                <div className="mt-6" style={{ padding: "1.25rem 1.25rem", border: "1px solid #e0e8f2", background: "#ffffff" }} data-testid="guide-related">
                  <div className="font-serif" style={{ color: "#1E1968", fontSize: "1.05rem", fontWeight: 600, lineHeight: 1.25 }}>
                    Related Guides
                  </div>
                  <ul className="mt-4 space-y-3.5" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {related.map((r) => {
                      const Icon = r.icon;
                      const tint = r.tint || { bg: "#eaf2fa", fg: "#1E1968" };
                      return (
                        <li key={r.to}>
                          <Link
                            to={r.to}
                            className="group flex items-start gap-3"
                            style={{ color: "#17233D", textDecoration: "none" }}
                            data-testid={`related-${r.to.replace(/[^a-z0-9]/gi, "-")}`}
                          >
                            {Icon && (
                              <span
                                aria-hidden="true"
                                style={{
                                  flex: "0 0 auto",
                                  width: 44, height: 44,
                                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                                  background: tint.bg, color: tint.fg,
                                  borderRadius: 999,
                                }}
                              >
                                <Icon style={{ width: 22, height: 22 }} strokeWidth={1.6} />
                              </span>
                            )}
                            <div className="min-w-0">
                              <div className="font-serif" style={{ color: "#1E1968", fontSize: "0.95rem", fontWeight: 600, lineHeight: 1.3 }}>
                                {r.label}
                              </div>
                              <div className="text-xs mt-1" style={{ color: "#3a4d70" }}>
                                {r.read || "Read more"}
                              </div>
                            </div>
                            <ArrowRight className="w-4 h-4 ml-auto mt-3 flex-none" style={{ color: "#258FC4" }} aria-hidden="true" />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}

/** Plain layout for pages that don't want a guide sidebar (e.g. Privacy). */
function PlainWrapper({ cta, ctaTo, children }) {
  return (
    <article className="summit-container py-12 md:py-16 max-w-3xl">
      {children}
      <div className="mt-14 pt-10" style={{ borderTop: "1px solid #e6edf5" }}>
        <Link to={ctaTo} className="btn-primary" data-testid="content-cta">
          {cta} <ArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
}

/* ------- Typography primitives — the guide pages use these as children ------- */

export const Eyebrow = ({ children }) => (
  <div className="section-pill mb-5" data-testid="content-eyebrow">{children}</div>
);
Eyebrow.displayName = "Eyebrow";

export const PageTitle = ({ children }) => (
  <h1
    className="font-serif"
    style={{
      color: "#1E1968",
      fontSize: "clamp(2.2rem, 4.5vw, 3.4rem)",
      fontWeight: 600,
      lineHeight: 1.06,
      letterSpacing: "-0.015em",
    }}
    data-testid="content-title"
  >
    {children}
  </h1>
);
PageTitle.displayName = "PageTitle";

export const Lead = ({ children }) => (
  <p
    className="mt-5 text-lg"
    style={{ color: "#17233D", maxWidth: 620, lineHeight: 1.55 }}
    data-testid="content-lead"
  >
    {children}
  </p>
);
Lead.displayName = "Lead";

// H2 renders a serif heading with a scroll-margin so the sticky header doesn't cover anchor jumps.
// First-in-article margin is kept comfortable via a :first-of-type trick in the article style.
export const H2 = ({ children }) => (
  <h2
    className="font-serif mt-12 first:mt-0"
    style={{
      color: "#1E1968",
      fontSize: "clamp(1.4rem, 2.4vw, 1.75rem)",
      fontWeight: 600,
      lineHeight: 1.2,
      scrollMarginTop: "100px",
    }}
  >
    {children}
  </h2>
);
H2.displayName = "H2";

export const P = ({ children, className = "" }) => (
  <p className={"mt-4 text-base " + className} style={{ color: "#17233D", lineHeight: 1.72 }}>
    {children}
  </p>
);
P.displayName = "P";

export const UL = ({ children }) => (
  <ul className="mt-4 pl-6 list-disc space-y-2 text-base" style={{ color: "#17233D", lineHeight: 1.65 }}>
    {children}
  </ul>
);
UL.displayName = "UL";

export default ContentPage;
